import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Zap, Check, Calendar as CalendarIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { usePaddleCheckout } from "@/hooks/usePaddleCheckout";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ideaId: string;
  ideaTitle: string;
  onSuccess?: () => void;
}

export default function BoostIdeaDialog({ open, onOpenChange, ideaId, ideaTitle, onSuccess }: Props) {
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [startMode, setStartMode] = useState<"now" | "scheduled">("now");
  const [startAt, setStartAt] = useState<string>(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  });
  const { user } = useAuth();
  const { openCheckout } = usePaddleCheckout();

  const { data: packages } = useQuery({
    queryKey: ["boost-packages"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("boost_packages")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as any[];
    },
  });

  const selectedPkg = packages?.find((p) => p.id === selected);

  const { data: availability } = useQuery({
    queryKey: ["boost-lane-availability", selectedPkg?.lane],
    enabled: !!selectedPkg?.lane,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("get_boost_lane_availability", {
        _lane: selectedPkg!.lane,
      });
      if (error) throw error;
      return Array.isArray(data) ? data[0] : data;
    },
  });

  const handleConfirm = async () => {
    if (!selected || !user) return;
    const pkg = selectedPkg;
    if (!pkg?.paddle_price_id) {
      toast.error("This boost isn't available for checkout yet.");
      return;
    }
    const scheduledStartAt =
      startMode === "scheduled" ? new Date(startAt).toISOString() : new Date().toISOString();
    if (new Date(scheduledStartAt).getTime() < Date.now() - 5 * 60 * 1000) {
      toast.error("Start time must be in the future.");
      return;
    }
    setBusy(true);
    try {
      await openCheckout({
        priceId: pkg.paddle_price_id,
        customerEmail: user.email ?? undefined,
        customData: { userId: user.id, ideaId, scheduledStartAt },
        successUrl: `${window.location.origin}/ideas/${ideaId}?boost=success`,
      });
      onOpenChange(false);
      setSelected(null);
      onSuccess?.();
    } catch (e: any) {
      toast.error("Boost failed", { description: e.message });
    } finally {
      setBusy(false);
    }
  };

  const endPreview = selectedPkg
    ? new Date(
        new Date(startMode === "scheduled" ? startAt : Date.now()).getTime() +
          selectedPkg.duration_days * 86400000
      )
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Zap className="h-5 w-5 text-amber-400" /> Boost this idea
          </DialogTitle>
          <DialogDescription>
            Surface "{ideaTitle}" in featured discovery lanes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {packages?.map((p) => {
            const isSel = selected === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setSelected(p.id)}
                className={`w-full text-left rounded-lg border p-3 transition-colors ${
                  isSel ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-foreground flex items-center gap-2">
                      {p.name}
                      {isSel && <Check className="h-4 w-4 text-primary" />}
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        {p.lane}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">{p.description}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-foreground">
                      ${(p.price_cents / 100).toFixed(0)}
                    </div>
                    <div className="text-xs text-muted-foreground">{p.duration_days}d</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {selectedPkg && (
          <div className="rounded-lg border border-border bg-background/40 p-3 space-y-3">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <CalendarIcon className="h-3.5 w-3.5" /> Schedule
              </div>
              {availability && (
                <span className="text-muted-foreground">
                  {(availability as any).open_slots} of {(availability as any).total} slots open in{" "}
                  <span className="text-foreground font-medium">{selectedPkg.lane}</span>
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant={startMode === "now" ? "default" : "outline"}
                onClick={() => setStartMode("now")}
              >
                Start now
              </Button>
              <Button
                type="button"
                size="sm"
                variant={startMode === "scheduled" ? "default" : "outline"}
                onClick={() => setStartMode("scheduled")}
              >
                Pick a date
              </Button>
            </div>
            {startMode === "scheduled" && (
              <div>
                <Label className="text-xs text-muted-foreground">Start (your local time)</Label>
                <Input
                  type="datetime-local"
                  value={startAt}
                  onChange={(e) => setStartAt(e.target.value)}
                />
              </div>
            )}
            {endPreview && (
              <div className="text-xs text-muted-foreground">
                Runs through{" "}
                <span className="text-foreground">
                  {endPreview.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                </span>
                . Goes live after admin review (usually under 24h).
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!selected || busy}>
            {busy ? "Processing…" : "Confirm boost"}
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground text-center">
          Payments in preview run in test mode — use card 4242 4242 4242 4242.
        </p>
      </DialogContent>
    </Dialog>
  );
}